import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import HttpClient from '@/api/HttpClient';

const httpClient = new HttpClient();

export type TvVendor = {
  id: number;
  name: string;
  code?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  active?: boolean;
};

export type TvPoLine = {
  id: number;
  inventoryItemId?: number;
  lineType: string;
  orderedQty: number;
  receivedQty: number;
  closedShortQty?: number;
  unitCostCents?: number | null;
  inventoryItem?: { id: number; upc: string; name?: string | null };
};

export type TvPurchaseOrder = {
  id: number;
  poNumber: string;
  status: string;
  orderedAt?: string;
  notes?: string | null;
  sourceImageS3Key?: string | null;
  sourceImageUrl?: string | null;
  vendor?: TvVendor;
  receiveLocation?: { id: number; name?: string | null };
  lines?: TvPoLine[];
};

export type TvPoOcrMatchedLine = {
  upc: string | null;
  sku: string | null;
  description: string | null;
  qty: number | null;
  unitCostDollars: number | null;
  confidence: number | null;
  matchedInventoryItem: { id: number; upc: string; name: string | null } | null;
  suggestedOrderedQty: number;
  suggestedUnitCostCents: number | null;
  matchSource?: 'upc' | 'alt_identifier' | null;
  searchCandidates?: Array<{ id: number; upc: string; name: string | null }>;
  searchQueryUsed?: string | null;
};

export type TvPoOcrExtract = {
  sourceImageS3Key: string;
  vendorName: string | null;
  poNumber: string | null;
  documentDate: string | null;
  matchedVendor: { id: number; name: string; code: string | null } | null;
  defaultReceiveLocationId: number | null;
  lines: TvPoOcrMatchedLine[];
};

export async function queryTomVendingVendors(
  search = '',
  options?: { includeInactive?: boolean },
): Promise<TvVendor[]> {
  const res = await httpClient.post('api/tom-vending/purchasing/vendor/query', {
    search,
    take: 500,
    includeInactive: options?.includeInactive ?? false,
  });
  return (res.data as { data?: TvVendor[] }).data ?? [];
}

export const useTomVendingVendorsList = (search = '', includeInactive = false) => {
  return useQuery({
    queryKey: ['tom-vending-vendors', search, includeInactive],
    queryFn: () => queryTomVendingVendors(search, { includeInactive }),
    staleTime: 60_000,
  });
};

export const useSaveTomVendingVendor = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vendor: Partial<TvVendor> & { name: string }) => saveTomVendingVendor(vendor),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tom-vending-vendors'] });
    },
  });
};

export async function saveTomVendingVendor(vendor: Partial<TvVendor> & { name: string }): Promise<TvVendor> {
  const res = await httpClient.post('api/tom-vending/purchasing/vendor/save', vendor);
  return (res.data as { vendor: TvVendor }).vendor;
}

export async function queryTomVendingPurchaseOrders(
  search = '',
  options?: { includeCancelled?: boolean },
): Promise<TvPurchaseOrder[]> {
  const res = await httpClient.post('api/tom-vending/purchasing/purchase-order/query', {
    search,
    take: 500,
    includeCancelled: options?.includeCancelled ?? false,
  });
  return (res.data as { data?: TvPurchaseOrder[] }).data ?? [];
}

export const useTomVendingPurchaseOrdersList = (search = '', includeCancelled = false) => {
  return useQuery({
    queryKey: ['tom-vending-purchase-orders', search, includeCancelled],
    queryFn: () => queryTomVendingPurchaseOrders(search, { includeCancelled }),
    staleTime: 30_000,
  });
};

export async function fetchTomVendingPurchaseOrder(id: number): Promise<TvPurchaseOrder> {
  const res = await httpClient.get('api/tom-vending/purchasing/purchase-order/details', { id });
  return (res.data as { purchaseOrder: TvPurchaseOrder }).purchaseOrder;
}

export type VendorItemUnitCostHint = {
  unitCostCents: number | null;
  source: 'po_line' | 'receipt_line' | 'catalog' | null;
};

export async function fetchTomVendingLastVendorUnitCost(
  vendorId: number,
  inventoryItemId: number,
): Promise<VendorItemUnitCostHint> {
  const res = await httpClient.get('api/tom-vending/purchasing/purchase-order/last-unit-cost', {
    vendorId,
    inventoryItemId,
  });
  return res.data as VendorItemUnitCostHint;
}

export async function createTomVendingPurchaseOrder(payload: {
  vendorId: number;
  receiveLocationId: number;
  poNumber: string;
  notes?: string;
  lines?: Array<{ inventoryItemId: number; orderedQty: number; unitCostCents?: number; lineType?: string }>;
}): Promise<TvPurchaseOrder> {
  const res = await httpClient.post('api/tom-vending/purchasing/purchase-order/create', {
    ...payload,
    lines: payload.lines ?? [],
  });
  return (res.data as { purchaseOrder: TvPurchaseOrder }).purchaseOrder;
}

export async function updateTomVendingPurchaseOrder(payload: {
  id: number;
  vendorId?: number;
  receiveLocationId?: number;
  poNumber?: string;
  notes?: string | null;
}): Promise<TvPurchaseOrder> {
  const res = await httpClient.post('api/tom-vending/purchasing/purchase-order/update', payload);
  return (res.data as { purchaseOrder: TvPurchaseOrder }).purchaseOrder;
}

export async function cancelTomVendingPurchaseOrder(id: number): Promise<TvPurchaseOrder> {
  const res = await httpClient.post('api/tom-vending/purchasing/purchase-order/cancel', { id });
  return (res.data as { purchaseOrder: TvPurchaseOrder }).purchaseOrder;
}

export async function addTomVendingPurchaseOrderLine(payload: {
  purchaseOrderId: number;
  inventoryItemId: number;
  orderedQty: number;
  unitCostCents?: number;
  lineType?: string;
}): Promise<TvPurchaseOrder> {
  const res = await httpClient.post('api/tom-vending/purchasing/purchase-order/line/add', payload);
  return (res.data as { purchaseOrder: TvPurchaseOrder }).purchaseOrder;
}

export async function updateTomVendingPurchaseOrderLine(payload: {
  id: number;
  inventoryItemId?: number;
  orderedQty?: number;
  unitCostCents?: number | null;
  lineType?: string;
}): Promise<TvPurchaseOrder> {
  const res = await httpClient.post('api/tom-vending/purchasing/purchase-order/line/update', payload);
  return (res.data as { purchaseOrder: TvPurchaseOrder }).purchaseOrder;
}

export async function deleteTomVendingPurchaseOrderLine(lineId: number): Promise<TvPurchaseOrder> {
  const res = await httpClient.post('api/tom-vending/purchasing/purchase-order/line/delete', { id: lineId });
  return (res.data as { purchaseOrder: TvPurchaseOrder }).purchaseOrder;
}

export async function submitTomVendingPurchaseOrder(id: number): Promise<TvPurchaseOrder> {
  const res = await httpClient.post('api/tom-vending/purchasing/purchase-order/submit', { id });
  return (res.data as { purchaseOrder: TvPurchaseOrder }).purchaseOrder;
}

export async function createAndPostTomVendingReceipt(payload: {
  purchaseOrderId: number;
  receiptNumber: string;
  lines: Array<{ purchaseOrderLineId: number; receivedQty: number; unitCostCents: number }>;
}): Promise<void> {
  const createRes = await httpClient.post('api/tom-vending/purchasing/goods-receipt/create', {
    purchaseOrderId: payload.purchaseOrderId,
    receiptNumber: payload.receiptNumber,
    lines: payload.lines,
  });
  const gr = (createRes.data as { goodsReceipt: { id: number } }).goodsReceipt;
  await httpClient.post('api/tom-vending/purchasing/goods-receipt/post', { id: gr.id });
}

export async function fetchTomVendingPoDocumentUploadUrl(
  fileExtension: string,
  contentType: string,
): Promise<{ uploadUrl: string; s3Key: string }> {
  const res = await httpClient.get('api/tom-vending/purchasing/po-document/upload-url', {
    fileExtension,
    contentType,
  });
  return res.data as { uploadUrl: string; s3Key: string };
}

export async function fetchTomVendingPoSourceImageUrl(
  purchaseOrderId: number,
): Promise<{ url: string; expiresInSeconds: number }> {
  const res = await httpClient.get('api/tom-vending/purchasing/purchase-order/source-image-url', {
    id: purchaseOrderId,
  });
  return res.data as { url: string; expiresInSeconds: number };
}

export async function extractTomVendingPoDocument(s3Key: string): Promise<TvPoOcrExtract> {
  const res = await httpClient.post('api/tom-vending/purchasing/po-document/extract', { s3Key });
  return (res.data as { extract: TvPoOcrExtract }).extract;
}

export async function createTomVendingPurchaseOrderFromExtract(payload: {
  vendorId: number;
  receiveLocationId: number;
  poNumber: string;
  notes?: string;
  sourceImageS3Key: string;
  lines: Array<{ inventoryItemId: number; orderedQty: number; unitCostCents?: number }>;
}): Promise<TvPurchaseOrder> {
  const res = await httpClient.post('api/tom-vending/purchasing/purchase-order/create-from-extract', payload);
  return (res.data as { purchaseOrder: TvPurchaseOrder }).purchaseOrder;
}

export async function closeShortTomVendingPurchaseOrder(payload: {
  id: number;
  reason?: string | null;
  lineIds?: number[];
}): Promise<TvPurchaseOrder> {
  const res = await httpClient.post('api/tom-vending/purchasing/purchase-order/close-short', payload);
  return (res.data as { purchaseOrder: TvPurchaseOrder }).purchaseOrder;
}

export type TomVendingInventorySettings = {
  costingMethod: string;
  defaultReceiveLocationId?: number | null;
  buyingAlertsEnabled: boolean;
  alertRecipientUserIds: number[];
  alertRecipients: Array<{ id: number; email: string; firstName?: string; lastName?: string }>;
};

export async function fetchTomVendingInventorySettings(): Promise<TomVendingInventorySettings> {
  const res = await httpClient.get('api/tom-vending/purchasing/settings', {});
  return (res.data as { settings: TomVendingInventorySettings }).settings;
}

export async function updateTomVendingInventorySettings(payload: {
  costingMethod?: string;
  defaultReceiveLocationId?: number | null;
  buyingAlertsEnabled?: boolean;
  alertRecipientUserIds?: number[];
}): Promise<TomVendingInventorySettings> {
  const res = await httpClient.post('api/tom-vending/purchasing/settings/update', payload);
  return (res.data as { settings: TomVendingInventorySettings }).settings;
}

export async function searchTomVendingAlertUsers(search: string) {
  const res = await httpClient.post('api/user/query', {
    search,
    take: 50,
    skip: 0,
    active: true,
    orderBy: 'email',
    orderDirection: 'ASC',
  });
  const data = (res.data as { data?: Array<{ id: number; email?: string; firstName?: string; lastName?: string }> })
    .data ?? [];
  return data
    .filter((u) => u.email?.trim())
    .map((u) => ({
      id: u.id,
      email: u.email!,
      firstName: u.firstName,
      lastName: u.lastName,
    }));
}

export async function listTomVendingKitComponents(parentInventoryItemId: number) {
  const res = await httpClient.get('api/tom-vending/purchasing/kit/list', {
    parentInventoryItemId,
  });
  return (res.data as { components: Array<{ componentQty: number; componentInventoryItem: { id: number; upc: string; name?: string | null } }> })
    .components;
}

export async function saveTomVendingKitComponents(
  parentInventoryItemId: number,
  components: Array<{ componentInventoryItemId: number; componentQty: number }>,
): Promise<void> {
  await httpClient.post('api/tom-vending/purchasing/kit/save', { parentInventoryItemId, components });
}

export async function queryTomVendingReplenishmentPolicies(locationId?: number) {
  const res = await httpClient.post('api/tom-vending/purchasing/replenishment/policy/query', {
    locationId,
    take: 500,
  });
  return (res.data as { data: unknown[] }).data ?? [];
}

export async function saveTomVendingReplenishmentPolicy(payload: Record<string, unknown>) {
  await httpClient.post('api/tom-vending/purchasing/replenishment/policy/save', payload);
}

export async function queryTomVendingBuyingSuggestions(opts?: {
  locationId?: number;
  triggerOnly?: boolean;
}) {
  const res = await httpClient.post('api/tom-vending/purchasing/replenishment/buying-suggestions', {
    triggerOnly: opts?.triggerOnly ?? false,
    locationId: opts?.locationId,
  });
  return (res.data as { data: unknown[] }).data ?? [];
}

export async function fetchTomVendingPolicyProposals(locationId?: number) {
  const res = await httpClient.get(
    'api/tom-vending/purchasing/replenishment/policy-proposals',
    locationId != null ? { locationId } : {},
  );
  return (res.data as { proposals: unknown[] }).proposals ?? [];
}

export async function acceptTomVendingPolicyProposal(id: number) {
  await httpClient.post('api/tom-vending/purchasing/replenishment/policy-proposal/accept', { id });
}

export async function dismissTomVendingPolicyProposal(id: number) {
  await httpClient.post('api/tom-vending/purchasing/replenishment/policy-proposal/dismiss', { id });
}
